/**
 * Money for the member portal.
 *
 * The API sends money as a decimal *string* — `{ amount: "1234.567890",
 * currency: "THB" }` — and this module never turns it into a `number`.
 * JavaScript numbers are IEEE-754 doubles: `0.1 + 0.2 !== 0.3`, and
 * `parseFloat("1234.567890")` is already an approximation. A customer noticing
 * that the portal and their invoice disagree by a satang is a trust problem
 * long before it is an accounting one.
 *
 * Internally everything is `bigint` micro-THB (1 THB = 1_000_000 µTHB), the
 * same representation the gateway uses in Redis and the same six-place
 * precision the contract specifies. Exact in, exact out.
 */

const MICRO = 1_000_000n;
const USAGE_DP = 6;
const DISPLAY_DP = 2;

/** Matches the contract's money pattern. Scientific notation is not money. */
const DECIMAL_RE = /^-?\d+(\.\d{1,6})?$/;

export class MoneyParseError extends Error {}

/** Decimal string -> µTHB, exactly. Never via `Number`. */
export function toMicro(amount: string): bigint {
  const s = amount.trim();
  if (!DECIMAL_RE.test(s)) {
    throw new MoneyParseError(`not a valid THB amount: ${JSON.stringify(amount)}`);
  }

  const negative = s.startsWith('-');
  const unsigned = negative ? s.slice(1) : s;

  // The regex above guarantees at most one '.', so indexOf is unambiguous.
  // Destructuring a split would be shorter but leaves the compiler unable to
  // prove the first element exists.
  const dot = unsigned.indexOf('.');
  const whole = dot === -1 ? unsigned : unsigned.slice(0, dot);
  const frac = dot === -1 ? '' : unsigned.slice(dot + 1);

  const micro = BigInt(whole) * MICRO + BigInt(frac.padEnd(USAGE_DP, '0'));
  return negative ? -micro : micro;
}

/** µTHB -> the 6-place decimal string the contract uses. */
export function toDecimalString(micro: bigint): string {
  const negative = micro < 0n;
  const abs = negative ? -micro : micro;
  const whole = abs / MICRO;
  const frac = (abs % MICRO).toString().padStart(USAGE_DP, '0');
  return `${negative ? '-' : ''}${whole}.${frac}`;
}

function groupThousands(digits: string): string {
  return digits.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
}

/**
 * Formats a balance or any customer-facing total: two decimal places, grouped.
 *
 * Two places because that is what appears on a tax invoice, and the portal
 * disagreeing with the invoice is exactly the confusion we are avoiding.
 */
export function formatBalance(amount: string): string {
  const micro = toMicro(amount);
  const negative = micro < 0n;
  const abs = negative ? -micro : micro;

  // Round half-up at the display boundary only. This is presentation, not
  // arithmetic — nothing downstream consumes it.
  const scale = MICRO / 100n;
  const rounded = (abs + scale / 2n) / scale;
  const whole = rounded / 100n;
  const cents = (rounded % 100n).toString().padStart(DISPLAY_DP, '0');

  return `${negative ? '-' : ''}${groupThousands(whole.toString())}.${cents}`;
}

/**
 * Formats a per-request cost.
 *
 * A single inference call costs a fraction of a satang. Rendering it at two
 * decimal places shows `0.00`, which reads as "free" and makes the usage table
 * look broken. So amounts below one satang keep enough precision to be a real
 * number, and only larger ones collapse to the familiar two places.
 */
export function formatUsageCost(amount: string): string {
  const micro = toMicro(amount);
  if (micro === 0n) return '0.00';

  const abs = micro < 0n ? -micro : micro;
  if (abs < MICRO / 100n) {
    // Below 0.01 THB — show the full six places rather than a misleading zero.
    return toDecimalString(micro);
  }
  return formatBalance(amount);
}

/** Sums decimal strings without ever leaving exact arithmetic. */
export function sum(amounts: string[]): string {
  return toDecimalString(amounts.reduce((acc, a) => acc + toMicro(a), 0n));
}

/**
 * True when the balance cannot cover a typical request.
 *
 * Deliberately not a "low balance" heuristic with a magic threshold: the server
 * owns that judgement. This only answers "is there anything left", which the UI
 * needs to decide whether to prompt for a top-up.
 */
export function isDepleted(amount: string): boolean {
  return toMicro(amount) <= 0n;
}

/** Renders with the currency, for headline figures. */
export function formatTHB(amount: string): string {
  return `฿${formatBalance(amount)}`;
}

const MICRO_PER_SATANG = 10_000n;

/**
 * Divides num by den and rounds to the nearest integer using round-half-to-
 * even (banker's rounding) — the same rule and reason
 * llmr-shared/pkg/money.RoundHalfEven gives: a service that disagreed with
 * another about which way 0.5 rounds would show up as an unreconciled
 * satang somewhere a customer could notice.
 */
function roundHalfEven(num: bigint, den: bigint): bigint {
  const q = num / den;
  const r = num % den;
  const twice = r * 2n;
  if (twice < den) return q;
  if (twice > den) return q + 1n;
  return q % 2n === 0n ? q : q + 1n; // exact tie: round to even
}

/**
 * Adds Thailand's 7% VAT on top of a VAT-exclusive THB amount, mirroring
 * llmr-shared/pkg/money.GrossUpVAT7 satang-for-satang (same banker's
 * rounding, same integer arithmetic — never a float) so this preview always
 * matches what member-api actually charges, not an independent estimate
 * that could disagree by a satang.
 *
 * Used only to show the customer a total before they submit — the real
 * charge is always computed server-side; nothing here is sent anywhere.
 *
 * Returns the same 6-place internal decimal string every other money.ts
 * function does (see sum()) — pass it to formatBalance/formatTHB for
 * display, same as a balance or a usage cost.
 *
 * Throws MoneyParseError if `amount` carries finer-than-satang precision (a
 * top-up amount never should — see topUpAmount's 2-decimal-place pattern in
 * TopUp.tsx).
 */
export function grossUpVAT7(amount: string): string {
  const micro = toMicro(amount);
  if (micro % MICRO_PER_SATANG !== 0n) {
    throw new MoneyParseError(`amount has finer than satang precision: ${amount}`);
  }
  const exVatSatang = micro / MICRO_PER_SATANG;
  const vatSatang = roundHalfEven(exVatSatang * 7n, 100n);
  const incVatSatang = exVatSatang + vatSatang;
  return toDecimalString(incVatSatang * MICRO_PER_SATANG);
}
